import type {
  AvailabilityResponse,
  CreateOrderInput,
  CreateOrderResponse,
  IngestEventsInput,
  MenuResponse,
  Problem,
} from '@cartazo/contracts'

/**
 * Cliente tipado de la API de Cartazo.
 *
 * Lo usan tanto la plantilla base inmersiva como los frontends a medida de
 * clientes premium: es el mismo contrato para ambos, que es lo que hace que la
 * plataforma sea headless de verdad y no solo de nombre.
 *
 * Sin dependencias más allá de `fetch`, para que pese lo mínimo en el bundle de
 * la carta.
 */

export class CartazoApiError extends Error {
  readonly status: number
  readonly problem: Problem | null

  constructor(status: number, problem: Problem | null, fallbackMessage: string) {
    super(problem?.detail ?? problem?.title ?? fallbackMessage)
    this.name = 'CartazoApiError'
    this.status = status
    this.problem = problem
  }

  /** El restaurante existe pero algo que pidió el comensal se acaba de agotar. */
  get isUnavailable(): boolean {
    return this.status === 409
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  get isRateLimited(): boolean {
    return this.status === 429
  }
}

export type CartazoClientOptions = {
  /** Base de la API, p. ej. `https://api.cartazo.app/v1`. */
  baseUrl: string
  /** Clave de partner. Solo para frontends a medida; la plantilla base no la usa. */
  apiKey?: string
  fetch?: typeof globalThis.fetch
  /** Corta la petición si el servidor no responde. Por defecto 10 s. */
  timeoutMs?: number
}

/** Respuesta que puede haber llegado sin cambios respecto al ETag enviado. */
export type Revalidated<T> = {
  /** `null` cuando el servidor respondió 304: lo que ya tenías sigue vigente. */
  data: T | null
  etag: string | null
  notModified: boolean
}

export function createCartazoClient(options: CartazoClientOptions) {
  const baseUrl = options.baseUrl.replace(/\/+$/, '')
  const doFetch = options.fetch ?? globalThis.fetch
  const timeoutMs = options.timeoutMs ?? 10_000

  async function request<T>(
    path: string,
    init: RequestInit & { etag?: string | null } = {},
  ): Promise<Revalidated<T>> {
    const { etag, ...rest } = init

    const headers = new Headers(rest.headers)
    headers.set('Accept', 'application/json')
    if (options.apiKey) headers.set('Authorization', `Bearer ${options.apiKey}`)
    // Con ETag, revisitar la carta cuesta ~200 bytes en vez de los 120 KB del árbol.
    if (etag) headers.set('If-None-Match', etag)

    // Un comensal en un sótano con una barra de señal no puede quedarse
    // esperando indefinidamente: mejor fallar y tirar de la copia cacheada.
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)

    let response: Response
    try {
      response = await doFetch(`${baseUrl}${path}`, {
        ...rest,
        headers,
        signal: rest.signal ?? controller.signal,
      })
    } finally {
      clearTimeout(timer)
    }

    if (response.status === 304) {
      return { data: null, etag: response.headers.get('ETag'), notModified: true }
    }

    if (!response.ok) {
      throw new CartazoApiError(response.status, await readProblem(response), response.statusText)
    }

    // 202 y 204 no traen cuerpo.
    const data = response.status === 204 || response.status === 202 ? null : await response.json()

    return { data: data as T, etag: response.headers.get('ETag'), notModified: false }
  }

  return {
    /**
     * Carta completa de un restaurante.
     *
     * Pasa el `etag` de la respuesta anterior para aprovechar el 304 y evitar
     * volver a descargar el árbol entero.
     */
    async getMenu(
      slug: string,
      params: { locale?: string; etag?: string | null } = {},
    ): Promise<Revalidated<MenuResponse>> {
      const query = params.locale ? `?locale=${encodeURIComponent(params.locale)}` : ''
      return request<MenuResponse>(`/menu/${encodeURIComponent(slug)}${query}`, {
        etag: params.etag ?? null,
      })
    },

    /**
     * Qué está agotado ahora mismo. Payload de 1–3 KB pensado para consultarse
     * cada ~45 s mientras la carta está visible.
     */
    async getAvailability(
      slug: string,
      params: { etag?: string | null } = {},
    ): Promise<Revalidated<AvailabilityResponse>> {
      return request<AvailabilityResponse>(`/menu/${encodeURIComponent(slug)}/availability`, {
        etag: params.etag ?? null,
      })
    },

    /**
     * Registra el pedido y devuelve el enlace de WhatsApp ya construido.
     *
     * `idempotencyKey` evita el duplicado clásico: mala señal, el comensal toca
     * "enviar" dos veces y el garzón sirve el plato dos veces.
     */
    async createOrder(
      slug: string,
      input: CreateOrderInput,
      opts: { idempotencyKey?: string } = {},
    ): Promise<CreateOrderResponse> {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey

      const result = await request<CreateOrderResponse>(
        `/menu/${encodeURIComponent(slug)}/orders`,
        { method: 'POST', headers, body: JSON.stringify(input) },
      )
      if (!result.data) throw new CartazoApiError(500, null, 'El servidor no devolvió el pedido')
      return result.data
    },

    /** Ingesta de analítica. Silenciosa por diseño: nunca debe romper la carta. */
    async ingestEvents(slug: string, input: IngestEventsInput): Promise<void> {
      await request(`/menu/${encodeURIComponent(slug)}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      })
    },
  }
}

export type CartazoClient = ReturnType<typeof createCartazoClient>

async function readProblem(response: Response): Promise<Problem | null> {
  // Un error puede venir de un proxy o un balanceador, no de nuestra API: en ese
  // caso el cuerpo no es un Problem Details y no debe hacer fallar el parseo.
  try {
    const contentType = response.headers.get('content-type') ?? ''
    if (!contentType.includes('json')) return null
    return (await response.json()) as Problem
  } catch {
    return null
  }
}

export type {
  AvailabilityResponse,
  CreateOrderInput,
  CreateOrderResponse,
  MenuResponse,
  Problem,
} from '@cartazo/contracts'
